// redux/user/publicProfile/userPublicProfileAPI.js

/**
 * API wrapper for public user profiles.
 * Points to backend: GET /api/v1/public/{id_or_slug}
 * Optional search falls back to /api/v1/search/ and extracts users.
 */

import apiClient from "../../authentication/utils/core/axios";

const API_PREFIX = "/public";

/**
 * Normalize backend PublicUserResponse defensively so the UI can trust the shape.
 * - Ensures credibility.top_sectors is [{ sector: string, score: number }]
 * - Fills in safe defaults for optional blocks
 */
export function normalizePublicProfile(raw) {
  if (!raw || typeof raw !== "object") return null;

  const credibility = raw.credibility || {};
  const topSectors = Array.isArray(credibility.top_sectors)
    ? credibility.top_sectors.map((s) => {
        // Backend sends: { name: string, score: number }
        // Frontend expects: { sector: string, score: number }
        
        let sectorName = s?.name || s?.sector; // Try both field names
        let score = s?.score;

        // Handle reversed data (legacy protection)
        if (typeof sectorName === "number" && typeof score === "string") {
          const tmp = sectorName;
          sectorName = score;
          score = tmp;
        }

        return {
          sector: typeof sectorName === "string" && sectorName.trim().length
            ? sectorName
            : "Unknown",
          score: typeof score === "number"
            ? score
            : Number.isFinite(Number(score))
            ? Number(score)
            : 0,
        };
      })
    : [];

  return {
    id: raw.id ?? null,
    username: raw.username ?? null,
    full_name: raw.full_name ?? null,
    professional_title: raw.professional_title ?? null,
    company: raw.company ?? null,
    location: raw.location ?? null,
    bio: raw.bio ?? null,
    website: raw.website ?? null,
    social_media_links:
      (raw.social_media_links && typeof raw.social_media_links === "object"
        ? raw.social_media_links
        : null),
    avatar_url: raw.avatar_url ?? null,

    credibility: {
      overall_score:
        typeof credibility.overall_score === "number"
          ? credibility.overall_score
          : Number.isFinite(Number(credibility.overall_score))
          ? Number(credibility.overall_score)
          : null,
      tier: credibility.tier ?? null,
      top_sectors: topSectors,
    },

    gamification: {
      level: raw?.gamification?.level ?? 1,
      total_points: raw?.gamification?.total_points ?? 0,
      badges_count: raw?.gamification?.badges_count ?? 0,
    },

    watchlist: {
      count: raw?.watchlist?.count ?? 0,
      top: Array.isArray(raw?.watchlist?.top) ? raw.watchlist.top : [],
    },

    recent_posts: Array.isArray(raw?.recent_posts) ? raw.recent_posts : [],

    stats: {
      posts: raw?.stats?.posts ?? 0,
      followers: raw?.stats?.followers ?? 0,
      following: raw?.stats?.following ?? 0,
      sentiment_mix: raw?.stats?.sentiment_mix ?? {
        bull: 0,
        bear: 0,
        neutral: 0,
      },
      top_tickers: Array.isArray(raw?.stats?.top_tickers)
        ? raw.stats.top_tickers
        : [],
      groups_public_owned: raw?.stats?.groups_public_owned ?? 0,
    },
  };
}

/**
 * Fetch a public profile by ID or slug.
 * @param {string|number} idOrSlug
 * @param {AbortSignal} [signal]
 */
async function fetchByIdOrSlug(idOrSlug, signal) {
  const safe = encodeURIComponent(String(idOrSlug));
  const res = await apiClient.get(`${API_PREFIX}/${safe}`, { signal });
  if (res.status !== 200) {
    throw new Error(
      `Failed to fetch public profile: HTTP ${res.status} ${res.statusText}`
    );
  }
  return normalizePublicProfile(res.data);
}

/**
 * Search users by username using the real search endpoint (/search-field/,
 * the only working search route -- /api/v1/search/ was dead code with zero
 * backend support and has since been deleted). '@' prefix puts the backend
 * in user-search mode (see search_engine.py's _is_user_query check).
 * @param {string} query
 * @param {AbortSignal} [signal]
 * @returns {Array<{ id: number, username: string, full_name?: string }>}
 */
async function searchPublicUsers(query, signal) {
  const q = query.startsWith("@") ? query : `@${query}`;
  const res = await apiClient.get(`/search-field/`, {
    params: { query: q },
    signal,
  });
  if (res.status !== 200) {
    throw new Error(`Failed to search users: HTTP ${res.status}`);
  }
  return Array.isArray(res.data?.users) ? res.data.users : [];
}

const UserPublicProfileAPI = {
  fetchByIdOrSlug,
  searchPublicUsers,
};

export default UserPublicProfileAPI;
